import {
  HomeIcon,
  LogoutIcon,
  PrescriptionIcon,
  StockRequestsIcon,
  StockTakeIcon,
  UserIcon,
  UserManagementIcon,
} from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { UnsavedDataConfirmationDialog } from "$routes/_lib/layout/unsaved-data-confirmation-dialog";
import { linkToHome, linkToSignOut } from "$routes/_lib/links";
import {
  linkToInventory,
  linkToInventoryMoveRequests,
} from "$routes/inventory/links";
import {
  linkToRegistersAnc,
  linkToRegistersChildImmunisation,
  linkToRegistersDownloadRequests,
  linkToRegistersNigeriaDailyOutpatient,
  linkToRegistersNigeriaGeneralAttendance,
  linkToRegistersPatient,
} from "$routes/registers/links";
import { linkToUserSettings } from "$routes/user/links";
import { ListItemIcon, ListItemText } from "@material-ui/core";
import ListItem from "@material-ui/core/ListItem";
import { Role } from "elephant-web/gql";
import React, { useState } from "react";
import { defineMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { linkToAdminControlPanel } from "../../../admin/links";
import { linkToClinicAdminControlPanel } from "../../../clinic-admin/links";
import { useSideMenuContext } from "./context";

const {
  Admin,
  Clinic,
  DataExporter,
  Doctor,
  Elephant,
  Pharmacist,
  Dispenser,
  Root,
  CashOfficer,
  Nurse,
  Technician,
} = Role;

const homeTranslation = defineMessage({
  id: "sidebar.home.label",
  defaultMessage: "Home",
});

const controlPanelTranslation = defineMessage({
  id: "sidebar.controlPanel.label",
  defaultMessage: "Control Panel",
});

const inventoryManagementTranslation = defineMessage({
  id: "sidebar.inventoryManagement.label",
  defaultMessage: "Inventory",
});

const patientRegisterTranslation = defineMessage({
  id: "sidebar.register.label",
  defaultMessage: "Patient Register",
});

const ancRegisterTranslation = defineMessage({
  id: "sidebar.ancRegister.label",
  defaultMessage: "ANC Register",
});

const childImmunisationRegisterTranslation = defineMessage({
  id: "sidebar.childImmunisationRegister.label",
  defaultMessage: "Child Immunisation Register",
});

const nigeriaDailyOutpatientRegisterTranslation = defineMessage({
  id: "sidebar.nigeriaDailyOutpatientRegister.label",
  defaultMessage: "Daily Outpatient Register",
});

const nigeriaGeneralAttendanceRegisterTranslation = defineMessage({
  id: "sidebar.nigeriaGeneralAttendanceRegister.label",
  defaultMessage: "General Attendance Register",
});

const registerDownloadsTranslation = defineMessage({
  id: "sidebar.registerDownloads.label",
  defaultMessage: "Register Downloads",
});

const userSettingsTranslation = defineMessage({
  id: "sidebar.userSettings.label",
  defaultMessage: "User Settings",
});

const logoutTranslation = defineMessage({
  id: "sidebar.logout.label",
  defaultMessage: "Log Out",
});

const stockInventoryRequestsTranslation = defineMessage({
  id: "sidebar.stockInventoryRequests.label",
  defaultMessage: "Inventory Requests",
});

type PrivateGuardProps = { userRole?: string };
const privateGuard =
  (roles: string[]) =>
  (Component: FC<PrivateGuardProps>) =>
  (props: PrivateGuardProps) => {
    const isAuthorised = roles?.includes(props.userRole);

    if (isAuthorised) {
      return <Component {...props} />;
    }

    return null;
  };

export const HomeLink = () => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToHome()} data-testid="SideMenu.ClinicianDashboard">
      <ListItemIcon>
        <HomeIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(homeTranslation)} />
    </SideMenuLink>
  );
};

export const AdminControlPanelLink = privateGuard([
  Admin,
  DataExporter,
  Elephant,
  Root,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToAdminControlPanel()}>
      <ListItemIcon>
        <UserManagementIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(controlPanelTranslation)} />
    </SideMenuLink>
  );
});

export const ClinicControlPanelLink = privateGuard([Clinic])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink
      to={linkToClinicAdminControlPanel()}
      data-testid="SideMenu.ClinicAdminControlPanel"
    >
      <ListItemIcon>
        <UserManagementIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(controlPanelTranslation)} />
    </SideMenuLink>
  );
});

export const InventoryLink = privateGuard([
  Nurse,
  Doctor,
  Pharmacist,
  Dispenser,
  Clinic,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToInventory()}>
      <ListItemIcon>
        <PrescriptionIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(inventoryManagementTranslation)} />
    </SideMenuLink>
  );
});

export const InventoryRequests = privateGuard([
  Nurse,
  Doctor,
  Pharmacist,
  Dispenser,
  Clinic,
  CashOfficer,
  Technician,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToInventoryMoveRequests()}>
      <ListItemIcon>
        <StockRequestsIcon />
      </ListItemIcon>
      <ListItemText
        primary={formatMessage(stockInventoryRequestsTranslation)}
      />
    </SideMenuLink>
  );
});

export const ViewPatientRegisterLink = privateGuard([Clinic])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToRegistersPatient()}>
      <ListItemIcon>
        <StockTakeIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(patientRegisterTranslation)} />
    </SideMenuLink>
  );
});

export const ViewRegisterDownloadRequestsLink = privateGuard([Clinic])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToRegistersDownloadRequests()}>
      <ListItemIcon>
        <StockTakeIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(registerDownloadsTranslation)} />
    </SideMenuLink>
  );
});

export const ViewAncRegisterLink = privateGuard([
  CashOfficer,
  Clinic,
  Dispenser,
  Doctor,
  Nurse,
  Pharmacist,
  Technician,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToRegistersAnc()}>
      <ListItemIcon>
        <StockTakeIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(ancRegisterTranslation)} />
    </SideMenuLink>
  );
});

export const ViewChildImmunisationRegisterLink = privateGuard([
  CashOfficer,
  Clinic,
  Dispenser,
  Doctor,
  Nurse,
  Pharmacist,
  Technician,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToRegistersChildImmunisation()}>
      <ListItemIcon>
        <StockTakeIcon />
      </ListItemIcon>
      <ListItemText
        primary={formatMessage(childImmunisationRegisterTranslation)}
      />
    </SideMenuLink>
  );
});

export const ViewNigeriaDailyOutpatientRegisterLink = privateGuard([
  Clinic,
  Doctor,
  Nurse,
  Pharmacist,
  CashOfficer,
  Technician,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToRegistersNigeriaDailyOutpatient()}>
      <ListItemIcon>
        <StockTakeIcon />
      </ListItemIcon>
      <ListItemText
        primary={formatMessage(nigeriaDailyOutpatientRegisterTranslation)}
      />
    </SideMenuLink>
  );
});

export const ViewNigeriaGeneralAttendanceRegisterLink = privateGuard([
  Clinic,
  Doctor,
  Nurse,
  Pharmacist,
  CashOfficer,
  Technician,
])(() => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToRegistersNigeriaGeneralAttendance()}>
      <ListItemIcon>
        <StockTakeIcon />
      </ListItemIcon>
      <ListItemText
        primary={formatMessage(nigeriaGeneralAttendanceRegisterTranslation)}
      />
    </SideMenuLink>
  );
});

export const UserSettingsLink = () => {
  const { formatMessage } = useIntl();

  return (
    <SideMenuLink to={linkToUserSettings()}>
      <ListItemIcon>
        <UserIcon />
      </ListItemIcon>
      <ListItemText primary={formatMessage(userSettingsTranslation)} />
    </SideMenuLink>
  );
};

export const LogoutLink = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { closeMenu } = useSideMenuContext();
  const {
    replicationState: { hasUnsyncedChanges },
  } = useRxDBContext();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const classes = useStyles();

  const handleLogoutClick = () => {
    if (hasUnsyncedChanges) {
      setShowConfirmDialog(true);
    } else {
      closeMenu();
      navigate(linkToSignOut());
    }
  };

  const handleConfirmLogout = () => {
    setShowConfirmDialog(false);
    closeMenu();
    navigate(linkToSignOut());
  };

  const handleCancelLogout = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <ListItem
        component="div"
        onClick={handleLogoutClick}
        classes={classes}
        data-testid="SideMenu.LogoutLink"
      >
        <ListItemIcon>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary={formatMessage(logoutTranslation)} />
      </ListItem>
      <UnsavedDataConfirmationDialog
        open={showConfirmDialog}
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    "&": `
      transition: background-color 0.3s ease-in;
    `,
    "&:hover": `
      background-color: ${theme.palette.grey[100]};
    `,
    color: theme.palette.text.primary,
  },
}));

interface Props {
  to?: string;
  href?: string;
  component?: React.ComponentType | string;
  children?: React.ReactNode;
  additionalMobileAppActions?: () => void;
}

const SideMenuLink: FC<Props> = ({
  component = Link,
  children,
  additionalMobileAppActions,
  ...props
}) => {
  const { closeMenu } = useSideMenuContext();
  const classes = useStyles();

  const closeMenuAndAdditionalActions = () => {
    additionalMobileAppActions();
    closeMenu();
  };

  return (
    <ListItem
      component={component as any}
      onClick={
        additionalMobileAppActions ? closeMenuAndAdditionalActions : closeMenu
      }
      classes={classes}
      {...props}
    >
      {children}
    </ListItem>
  );
};
